/* global axios */
import ApiClient from './ApiClient';

// Thin client for the Telegram-specific message actions (#160).
// Each endpoint lives under
//   /api/v1/accounts/:account_id/conversations/:conv_id/telegram_actions/...
// and returns { ok: boolean, error?: string, message?: object }.
class TelegramActionsAPI extends ApiClient {
  constructor() {
    // ApiClient builds the base /api/v1/accounts/:account_id/... prefix for us.
    super('conversations', { accountScoped: true });
  }

  _url(conversationId, action) {
    return `${this.url}/${conversationId}/telegram_actions/${action}`;
  }

  // Edit message text (within 48h, own outgoing only).
  editMessage(conversationId, messageId, content) {
    return axios.post(this._url(conversationId, 'edit'), {
      message_id: messageId,
      content,
    });
  }

  // Delete via Telegram + flag locally as deleted.
  deleteMessage(conversationId, messageId) {
    return axios.post(this._url(conversationId, 'delete'), {
      message_id: messageId,
    });
  }

  // Forward to another Telegram conversation.
  forwardMessage(conversationId, messageId, targetConversationId) {
    return axios.post(this._url(conversationId, 'forward'), {
      message_id: messageId,
      target_conversation_id: targetConversationId,
    });
  }

  // Set / remove reaction. Empty emoji removes.
  reactToMessage(conversationId, messageId, emoji) {
    return axios.post(this._url(conversationId, 'react'), {
      message_id: messageId,
      emoji,
    });
  }

  // Pin / unpin in the chat.
  pinMessage(conversationId, messageId, action = 'pin') {
    return axios.post(this._url(conversationId, 'pin'), {
      message_id: messageId,
      action,
    });
  }

  // Tell Telegram that the user has read the listed message ids.
  // Best-effort; backend swallows individual failures.
  readBusiness(conversationId, messageIds) {
    return axios.post(this._url(conversationId, 'read_business'), {
      message_ids: messageIds,
    });
  }

  // MTProto only: pull older history for this chat from Telegram into the DB
  // (backfill). `before` = the oldest already-loaded Telegram message id
  // (scroll-up); omit/0 = from the newest (open-chat). Resolves once the
  // messages are ingested, so the caller re-reads the conversation afterwards.
  // Returns { ok, ingested, oldest_id }.
  loadMtprotoHistory(conversationId, { limit = 30, before = 0 } = {}) {
    return axios.post(this._url(conversationId, 'mtproto_history'), {
      limit,
      before,
    });
  }

  // MTProto only: ask the server to fetch the contact's CURRENT online status
  // (the live UpdateUserStatus event only fires on change). Fire-and-forget — the
  // result arrives via the normal presence path (contact.updated → header live).
  fetchPresence(conversationId) {
    return axios.post(this._url(conversationId, 'presence'), {});
  }
}

export default new TelegramActionsAPI();
